import { LocaleCodeSchema } from './chunk-BTYFPOCZ.js';
import { z } from 'zod';

var AuthStrategySchema = /* @__PURE__ */ z.enum([
  "jwt",
  "session",
  "api-key",
  "oauth",
  "public",
  "unknown"
]);
var AuthProviderSchema = /* @__PURE__ */ z.enum(["auth0", "clerk", "cognito", "workos", "supertokens"]);
var AuthFlowAnnotationSchema = /* @__PURE__ */ z.object({
  strategy: AuthStrategySchema,
  /** Library tag — omitted when no provider-specific marker hit. */
  provider: AuthProviderSchema.optional(),
  /**
   * CS-101c — how sure the detector is of this auth-flow annotation, in `[0, 1]`.
   * Lets downstream serving/QA weight a detected annotation by detector certainty.
   * Uniform with the sibling `job`/`pagination`/`rateLimit` annotations; mirrors
   * {@link Arg.confidence} (per-field). Optional + additive — absent when no
   * confidence was recorded (no producer sets it yet; the CLI detectors will in
   * follow-up detector work). `version` stays 1.
   */
  confidence: z.number().min(0).max(1).optional()
});
function isPublicCapability(capability) {
  return capability.requiresAuth === false || capability.auth?.strategy === "public";
}
var JobProviderSchema = /* @__PURE__ */ z.enum([
  "bull",
  "inngest",
  "temporal",
  // AF-35 — Python + Ruby job frameworks
  "celery",
  "sidekiq",
  // Cross-language broadening — PHP (Laravel queues) + Java/Kotlin (Spring @Async
  // + messaging templates). Additive enum values; `version` stays 1.
  "laravel",
  "spring"
]);
var JobAnnotationSchema = /* @__PURE__ */ z.object({
  provider: JobProviderSchema,
  /**
   * Canonical job identifier when the framework + handler combo
   * lets the detector capture it (Bull job name, Inngest event
   * name, Temporal workflow function identifier). Omitted when
   * the detector can't capture it (variable payload, etc.).
   */
  jobName: z.string().min(1).max(120).optional(),
  /**
   * CS-101c — how sure the detector is of this job annotation, in `[0, 1]`. Lets
   * downstream serving/QA weight a detected annotation by detector certainty.
   * Uniform with the sibling `auth`/`pagination`/`rateLimit` annotations; mirrors
   * {@link Arg.confidence} (per-field). Optional + additive — absent when no
   * confidence was recorded (no producer sets it yet; the CLI detectors will in
   * follow-up detector work). `version` stays 1.
   */
  confidence: z.number().min(0).max(1).optional()
});
var PaginationStyleSchema = /* @__PURE__ */ z.enum(["cursor", "offset", "page", "link-header"]);
var PaginationAnnotationSchema = /* @__PURE__ */ z.object({
  style: PaginationStyleSchema,
  /**
   * The driving query-parameter name when the style is param-based
   * (`cursor` / `offset` / `page`). Omitted for `link-header` (no param drives it).
   */
  param: z.string().min(1).max(60).optional(),
  /**
   * CS-101c — how sure the detector is of this pagination annotation, in `[0, 1]`.
   * Lets downstream serving/QA weight a detected annotation by detector certainty.
   * Uniform with the sibling `auth`/`job`/`rateLimit` annotations; mirrors
   * {@link Arg.confidence} (per-field). Optional + additive — absent when no
   * confidence was recorded (no producer sets it yet; the CLI detectors will in
   * follow-up detector work). `version` stays 1.
   */
  confidence: z.number().min(0).max(1).optional()
});
var RateLimitSourceSchema = /* @__PURE__ */ z.enum(["decorator", "middleware", "header"]);
var RateLimitAnnotationSchema = /* @__PURE__ */ z.object({
  source: RateLimitSourceSchema,
  /**
   * CS-101c — how sure the detector is of this rate-limit annotation, in `[0, 1]`.
   * Lets downstream serving/QA weight a detected annotation by detector certainty.
   * Uniform with the sibling `auth`/`job`/`pagination` annotations; mirrors
   * {@link Arg.confidence} (per-field). Optional + additive — absent when no
   * confidence was recorded (no producer sets it yet; the CLI detectors will in
   * follow-up detector work). `version` stays 1.
   */
  confidence: z.number().min(0).max(1).optional()
});
var WebhookKindSchema = /* @__PURE__ */ z.enum(["inbound", "outbound"]);
var WebhookProviderSchema = /* @__PURE__ */ z.enum(["stripe", "github", "twilio", "slack"]);
var WebhookConfidenceSchema = /* @__PURE__ */ z.enum(["verified", "pattern"]);
var WebhookAnnotationSchema = /* @__PURE__ */ z.object({
  kind: WebhookKindSchema,
  /** Null when the body marker is generic-HMAC (provider-agnostic). */
  provider: WebhookProviderSchema.nullable(),
  confidence: WebhookConfidenceSchema
});
var ArgTypeSchema = /* @__PURE__ */ z.enum(["string", "number", "boolean", "array", "object", "unknown"]);
var ArgSchema = /* @__PURE__ */ z.lazy(
  () => z.object({
    name: z.string().min(1),
    type: ArgTypeSchema,
    required: z.boolean(),
    description: z.string().max(140).optional(),
    /**
     * Ask-spec phase 2 — the human name for this argument, shown to a VISITOR
     * as the composed form's field label (ADR-0552) and served as the JSON
     * Schema `title`. Absent ⇒ consumers fall back to the argument KEY, which
     * is what the form does today: the server knows no better name, and
     * inventing one would be inventing meaning about the customer's API.
     *
     * **Server-set only, and structurally so.** The single writer is
     * `applyOverrideFields`, from the operator's `catalog:edit`-gated,
     * write-boundary-sanitized override row. A CLI can put the key in an
     * uploaded body — `uploadCapabilitySchema` takes `body` as opaque — but
     * `marshalArg` is an explicit allowlist and does not carry it, so an
     * uploaded label never survives the row→Capability read-back. That is the
     * mechanism; do NOT "complete" the marshaller by adding it, or a
     * visitor-facing string acquires a second, unsanitized writer.
     */
    label: z.string().min(1).optional(),
    example: z.unknown().optional(),
    enum: z.array(z.string()).min(1).optional(),
    format: z.string().optional(),
    minimum: z.number().optional(),
    maximum: z.number().optional(),
    pattern: z.string().optional(),
    sdlType: z.string().min(1).optional(),
    provenance: SchemaSourceSchema.optional(),
    confidence: z.number().min(0).max(1).optional(),
    pii: z.boolean().default(false),
    shape: z.array(ArgSchema).optional(),
    items: ArgSchema.optional(),
    /**
     * E2 / ADR-0783 — operator-authored through the argument override
     * (`argument-override.ts`), merged onto the served capability by
     * `applyArgumentOverrides`. `askIf: 'absent'` asks the visitor for the
     * argument on every class when the model left it out; `default` is the
     * value the server sends when the model supplied none.
     */
    askIf: z.literal("absent").optional(),
    default: z.unknown().optional()
  })
);
var GraphqlInputObjectSchema = /* @__PURE__ */ z.object({
  argName: z.string().min(1),
  sdlType: z.string().min(1)
});
var WsReplySchema = /* @__PURE__ */ z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("ack") }),
  z.object({ kind: z.literal("event"), responseEvent: z.string().min(1) }),
  z.object({ kind: z.literal("none") })
]);
var RestBodyEncodingSchema = /* @__PURE__ */ z.enum(["json", "form", "multipart", "binary"]);
var HttpMethodSchema = /* @__PURE__ */ z.enum([
  "GET",
  "POST",
  "PUT",
  "PATCH",
  "DELETE",
  "OPTIONS",
  "HEAD"
]);
var GraphQLOpSchema = /* @__PURE__ */ z.enum([
  "GRAPHQL_QUERY",
  "GRAPHQL_MUTATION",
  "GRAPHQL_SUBSCRIPTION"
]);
var GrpcOpSchema = /* @__PURE__ */ z.enum([
  "GRPC_UNARY",
  "GRPC_SERVER_STREAMING",
  "GRPC_BIDI_STREAMING",
  // CS-314 — client-streaming (`rpc X(stream Req) returns (Resp)`): the client
  // streams multiple request messages to a unary response. Previously downgraded
  // to GRPC_UNARY; now classified distinctly (and degraded, like the other
  // streaming kinds, on the server-side execution path — it is not unary-callable).
  "GRPC_CLIENT_STREAMING"
]);
var TrpcOpSchema = /* @__PURE__ */ z.enum(["TRPC_QUERY", "TRPC_MUTATION", "TRPC_SUBSCRIPTION"]);
var WsOpSchema = /* @__PURE__ */ z.enum(["WS_HANDLER"]);
var SseOpSchema = /* @__PURE__ */ z.enum(["SSE_STREAM"]);
var FunctionOpSchema = /* @__PURE__ */ z.enum(["FUNCTION"]);
var WpAjaxOpSchema = /* @__PURE__ */ z.enum(["WP_AJAX"]);
var MethodSchema = /* @__PURE__ */ z.union([
  HttpMethodSchema,
  GraphQLOpSchema,
  GrpcOpSchema,
  TrpcOpSchema,
  WsOpSchema,
  SseOpSchema,
  FunctionOpSchema,
  WpAjaxOpSchema
]);
var FrameworkSchema = /* @__PURE__ */ z.enum([
  "nestjs",
  "express",
  "fastify",
  "fastapi",
  "nextjs",
  "graphql",
  "sdk-tool",
  // AF-38
  "grpc",
  "trpc",
  "websocket",
  // AF-33 — server-framework deep extractors
  "springboot",
  "django",
  "flask",
  "rails",
  "phoenix",
  "gin",
  // AF-401 — first-class Go frameworks (own tree-sitter/source extractors,
  // disambiguated from Gin by import path). `net-http` is hyphenated to match
  // the existing `sdk-tool` id style.
  "chi",
  "echo",
  "fiber",
  "net-http",
  "actix",
  "axum",
  "laravel",
  "aspnet",
  // AF-119 — import command (OpenAPI spec import)
  "openapi",
  // AF-396/397 — Spring Boot Kotlin tree-sitter (kotlin uses springboot framework id;
  //              'kotlin' added for Ktor-native apps)
  "kotlin",
  // AF-416 — long-tail batch A
  "vapor",
  "play",
  "compojure",
  // AF-417 — long-tail batch B
  "dream",
  "servant",
  "cowboy",
  "plumber",
  "lapis",
  // Newly added popular frameworks.
  "hono",
  "symfony",
  "koa",
  "hapi",
  "elysia",
  "sinatra",
  "litestar",
  "ktor",
  // Import sources (not a runtime framework — the surface came from a spec/collection).
  "postman",
  "serverless",
  "kubernetes",
  "har",
  // CS-302c-write — a runtime-witness-observed endpoint an operator PROMOTED into
  // the authored catalog (not statically scanned; provenance is `inferred-runtime`).
  "runtime-witness",
  // SYN-448 (ADR-0316) — a non-route exported function surfaced as a capability
  // by the SYN-349 CLI internal-function scan (method `FUNCTION`). NOT a runtime
  // framework and NOT a static ROUTE extractor: it has no F1 fixtures, so it is
  // deliberately excluded from `CliExtractableFramework` in the eval harness
  // (`packages/cli/src/eval/discovery-runner.ts`), like `sdk-tool` /
  // `runtime-witness`.
  "internal-function",
  // Frontend-only discovery mechanisms (PLAN `plan/frontend-only-discovery/`).
  // NOT runtime frameworks and NOT backend route extractors: each names the
  // MECHANISM that derived the capability from CLIENT source alone, in the same
  // way `openapi` / `postman` / `har` name an import mechanism and
  // `runtime-witness` names an observation mechanism.
  //
  // These values are load-bearing for SECURITY, not only for audit: the server
  // reads them (and only them) to apply the ADR-0202 § 3 always-confirm floor to
  // capabilities that carry no backend-source risk label — see
  // `FRONTEND_DERIVED_FRAMEWORKS` below. Adding a member here therefore widens
  // what gets floored, which is the safe direction; removing one un-floors a
  // mechanism and is a security change.
  //
  // Excluded from `CliExtractableFramework` in the eval harness for the same
  // reason `internal-function` is — they have no backend F1 route fixtures and
  // are scored by the dedicated frontend eval tier instead
  // (`packages/cli/src/eval/frontend/`, fixtures at
  // `packages/cli/__fixtures__/frontend/`).
  "ui-api-map",
  "form-flow",
  "route-map",
  "graphql-client",
  "trpc-client",
  "realtime-client"
]);
var FRONTEND_DERIVED_FRAMEWORKS = /* @__PURE__ */ new Set([
  "ui-api-map",
  "form-flow",
  "route-map",
  "graphql-client",
  "trpc-client",
  "realtime-client"
]);
function isFrontendDerivedFramework(framework) {
  if (framework === void 0) return false;
  return FRONTEND_DERIVED_FRAMEWORKS.has(framework);
}
var SideEffectSchema = /* @__PURE__ */ z.enum(["read", "write", "destructive"]);
var RiskSchema = /* @__PURE__ */ z.enum(["low", "medium", "high", "critical"]);
var DataFreshnessSchema = /* @__PURE__ */ z.enum(["live", "static", "configurable"]);
var SchemaSourceSchema = /* @__PURE__ */ z.enum([
  // Declared-schema sources — the arg's type was read from an explicit contract.
  "zod",
  "class-validator",
  "pydantic",
  "graphql-input",
  "graphql-type",
  "openapi",
  // CS-101a — the §7 untyped-inference ladder. The single `inferred` bucket is
  // split into three provenance rungs so highest-confidence-wins fusion can
  // carry mixed-provenance fields (static AST inference vs runtime witness vs
  // LLM guess). Producers emit ONE of these three going forward; the rung feeds
  // the per-field confidence band (see {@link Arg.confidence}).
  "inferred-static",
  "inferred-runtime",
  "inferred-llm",
  // WF-4.69 (D28 rung 5) — the USAGE reading: what the client's own code does
  // with the result. `const { id, status } = await res.json()` names two fields
  // the app depends on; it says nothing about the other thirty-eight.
  //
  // Its own member rather than `inferred-static`, and that is the whole reason
  // this token exists. Rung 4 (a TS type argument) ALREADY emits
  // `inferred-static`, so a usage reading sharing it would collapse exactly the
  // distinction `call-shapes.ts` refuses to lose — "the author DECLARED this"
  // and "the author READ these two fields" are different claims with different
  // failure modes, and one token for both makes the provenance meaningless.
  //
  // A producer emitting this MUST also set `Shape.exhaustive` to `false`: the
  // rung is a DEPENDENCY SET, and the two fields travel together or the set
  // reads as a schema.
  "inferred-usage",
  // WF-4.69 (D28) — `response-unknown`: the ladder RAN on this shape and
  // reached no rung.
  //
  // A reported state rather than an absent field, the discipline `unmeasured`
  // takes on the fit-outcome envelope and "not seen" takes on the site manual.
  // An absent `response` says nobody looked, which is a different fact with a
  // different next step, and rendering both as absence is how a gap in the
  // ladder becomes indistinguishable from a gap in the coverage.
  //
  // Generic rather than `response-unknown` because a BODY can be unresolved on
  // the same terms, and a second token for the same state on the other side of
  // one call is a vocabulary that drifts.
  "unresolved",
  // Ask-spec phase 2 — a HUMAN stated it. Not a schema and not an inference:
  // the operator's own per-argument override (`argument-override.ts`), stamped
  // by `applyOverrideFields` when the merge applies one. It outranks every
  // member above it in `isEnforceableRequired`, because an operator is the
  // CUSTOMER and a confidence score describing an extractor's reading of their
  // code must never suppress their explicit correction of it.
  //
  // Wire-settable like every other member, and that is unchanged exposure
  // rather than new: a client claiming `zod` on a guess already gets
  // enforcement (the conservative direction ADR-0551 accepted), so claiming
  // this one grants nothing it did not have. What it must not do is let a lie
  // render as truth — the dashboard reads "the operator set this" from the
  // OVERRIDE ROW, never from a served arg's provenance.
  "operator",
  // Legacy READ alias (decision D3): pre-CS-101a catalogs persisted a bare
  // `inferred` in `Shape.schemaSource` / arg provenance. Kept accepted on read
  // so already-stored values still parse — NEVER emitted by new producers.
  "inferred",
  // No schema source at all. Predates the ladder and is NOT its "we could not
  // tell" answer — that is `unresolved`, above, which says the ladder ran.
  "none"
]);
var CAPABILITY_PROVENANCES = ["drift", "spec-import", "ui-discovery"];
var CapabilityProvenanceSchema = /* @__PURE__ */ z.enum(CAPABILITY_PROVENANCES);
var CapabilitySourceSchema = /* @__PURE__ */ z.object({
  framework: FrameworkSchema,
  filePath: z.string().min(1),
  lineNumber: z.number().int().positive(),
  handlerName: z.string().optional(),
  confidence: z.number().min(0).max(1),
  /**
   * CS-101f — the originating service / workspace package this capability was
   * discovered in (a monorepo package name like `@acme/orders-api`, or a
   * microservice name). A SINGLE value per capability: one capability comes from
   * one service. Unlike `filePath` / `lineNumber` / `handlerName` (code locations
   * redacted on upload), `service` is NOT sensitive — it is a package name needed
   * downstream — so it SURVIVES the upload-source redaction (see
   * `prepareUploadCapability` / `uploadCapabilitySchema`). This is the schema atom
   * CS-102 (multi-service composition) reads to namespace capability ids and union
   * per-service catalogs. Optional + additive: legacy single-scan catalogs and
   * scans that cannot resolve a package name leave it absent.
   */
  service: z.string().min(1).optional(),
  /** ADR-0400 — see {@link CapabilityProvenanceSchema}. Server-set on every transport (SYN-834). */
  provenance: CapabilityProvenanceSchema.optional(),
  /**
   * ADR-0590 — a publish path RESOLVED this capability's risk class and seeded
   * the governance default the customer owns.
   *
   * Read only alongside `provenance: 'ui-discovery'`, where it narrows ADR-0400's
   * always-confirm floor from a refusal into a default. Absent ⇒ the floor stays
   * structural, which is why it is a POSITIVE marker: every row that predates it,
   * every CLI upload and every legacy drafter addition keeps ADR-0400 unchanged.
   *
   * **It is deliberately NOT `sideEffect !== null` on the served row**, which is
   * the shape this started as and which conflates two different facts. Every
   * upload runs through `resolveSideEffectClass` at ingest (SYN-45), so a served
   * `sideEffect` is present for the whole catalog and says only that the METHOD
   * FLOOR was applied — nobody examined the capability. Keying the narrowing on
   * it would have silently stripped the floor from the CLI frontend-derived lane
   * (`FRONTEND_DERIVED_FRAMEWORKS`), which carries `ui-discovery` provenance and
   * receives none of ADR-0590's compensating controls, because it never reaches
   * the publish writer that seeds them.
   *
   * The fact behind it is the ADDITION row's own `side_effect`, which is NULLABLE
   * and which only a publish can set — so it genuinely records "a publish
   * resolved a class here", and `served-catalog.ts` composes it server-side into
   * the same fixed `source` literal that already carries `provenance`. Like
   * `provenance` it is server-set on every transport: the CLI upload allowlist
   * strips unknown `source` keys, and `sanitizeGraphqlCapabilitySource` refuses
   * it by name.
   */
  classified: z.boolean().optional(),
  /**
   * B5 — HOW a walk-discovered capability was learned, flattened across the
   * discovery lane and (for the form lane) which of its four legs produced the
   * descriptor. Server-set on every transport, like the two above.
   *
   * A DISCLOSURE and never a gate: nothing ranks these and nothing keys a
   * decision on one. The risk class, the enablement default and the confirm
   * floor are all decided from the method and the destructive vocabulary, so a
   * capability learned the weakest way (a literal in minified handler source) is
   * governed exactly like one learned the strongest (a rendered form with a
   * browser-enforced schema). It exists so the operator approving it can be told
   * which of those two they are looking at.
   *
   * Absent means "not recorded" — every capability that is not a walk publish,
   * and every row predating the column. Deliberately not a member.
   */
  learnedFrom: z.string().optional()
});
var ShapeSchema = /* @__PURE__ */ z.object({
  shape: z.array(ArgSchema),
  schemaSource: SchemaSourceSchema.optional(),
  /**
   * WF-4.69 (D28) — whether this field list is the WHOLE contract.
   *
   * `true` — the source enumerates it: a runtime validator, a declared
   * TypeScript type, an OpenAPI schema. What is not listed is not there.
   *
   * `false` — a DEPENDENCY SET, not a schema. These are the fields the client
   * was observed to need; the endpoint returns at least them and probably more.
   * Every `inferred-usage` shape is one, and the flag is what stops a
   * two-field destructuring read being served as "the response is
   * `{id, status}`" — the denominator error, committed against our own output.
   *
   * Absent — not recorded. Every shape predating this marker, and the honest
   * answer for a producer that has not been taught to say. A consumer must
   * treat absent as UNKNOWN rather than as `true`: assuming exhaustive is the
   * direction that manufactures a claim, and the flag exists because that claim
   * is invisible once made.
   */
  exhaustive: z.boolean().optional(),
  /**
   * Set ONLY when this body shape is the FLATTENED GraphQL sole-input-object
   * idiom (act-as-user PR-17.2 follow-up). Carries the wrapper field-arg name +
   * its SDL type so the issuer can thread it onto the operation and the widget
   * can rebuild + execute the mutation. See {@link GraphqlInputObjectSchema}.
   */
  graphqlInputObject: GraphqlInputObjectSchema.optional()
});
var StreamingDescriptorSchema = /* @__PURE__ */ z.object({
  direction: z.enum(["server-to-client"]),
  payloadShape: ShapeSchema.optional()
});
function localizedText(max) {
  return z.record(LocaleCodeSchema, z.string().min(1).max(max));
}
var TRANSLATION_EXPANSION_FACTOR = 1.5;
var CANONICAL_TITLE_MAX = 60;
var CANONICAL_DESCRIPTION_MAX = 280;
var CANONICAL_ARG_DESCRIPTION_MAX = 140;
var expandCap = (canonical) => Math.round(canonical * TRANSLATION_EXPANSION_FACTOR);
var LOCALIZED_TITLE_MAX = expandCap(CANONICAL_TITLE_MAX);
var LOCALIZED_DESCRIPTION_MAX = expandCap(CANONICAL_DESCRIPTION_MAX);
var LOCALIZED_ARG_DESCRIPTION_MAX = expandCap(CANONICAL_ARG_DESCRIPTION_MAX);
var LocalizedTextSchema = localizedText(LOCALIZED_DESCRIPTION_MAX);
var CapabilityLocalizationSchema = /* @__PURE__ */ z.object({
  // An identifier, not prose — the enrichment contract explicitly instructs the
  // model never to translate it, so it keeps the canonical 60-char cap.
  name: localizedText(CANONICAL_TITLE_MAX).optional(),
  title: localizedText(LOCALIZED_TITLE_MAX).optional(),
  description: localizedText(LOCALIZED_DESCRIPTION_MAX).optional(),
  // Per-argument descriptions: arg-name → per-locale text. Keyed by a plain
  // string (arg names are not a fixed enum).
  argDescriptions: z.record(z.string().min(1), localizedText(LOCALIZED_ARG_DESCRIPTION_MAX)).optional()
});
var MAX_SYNTHETIC_QUERIES = 8;
var SyntheticQueriesSchema = /* @__PURE__ */ z.record(
  LocaleCodeSchema,
  z.array(z.string().min(1).max(120)).max(MAX_SYNTHETIC_QUERIES)
);
var LatencyMsSchema = /* @__PURE__ */ z.number().int().positive();
var CapabilityExampleSchema = /* @__PURE__ */ z.object({
  description: z.string().max(200).optional(),
  args: z.record(z.string(), z.unknown())
});
var MAX_CAPABILITY_EXAMPLES = 5;
var CapabilityExamplesSchema = /* @__PURE__ */ z.array(CapabilityExampleSchema).max(MAX_CAPABILITY_EXAMPLES);
var CapabilityPrioritySchema = /* @__PURE__ */ z.enum(["high", "normal", "low"]);
var CAPABILITY_ID_PATTERN = /^[a-z][a-z0-9-]*\.[a-z][a-z0-9-]*$/;
var CapabilitySchema = /* @__PURE__ */ z.object({
  // Identity
  id: z.string().regex(CAPABILITY_ID_PATTERN),
  version: z.literal(1),
  // Origin
  source: CapabilitySourceSchema,
  // Endpoint
  method: MethodSchema,
  path: z.string().min(1),
  // Shape
  pathParams: z.array(ArgSchema).default([]),
  queryParams: z.array(ArgSchema).default([]),
  body: ShapeSchema.optional(),
  response: ShapeSchema.optional(),
  // Semantics (filled by LLM enrichment in Phase 1 pass 7; optional pre-enrich)
  name: z.string().min(1).max(60).optional(),
  title: z.string().min(1).max(60).optional(),
  description: z.string().max(280).optional(),
  // CS-101b (decision D2) — a SEPARATE, agent-only hint distinct from the
  // human-facing `description`. The 280-char `description` doubles as dashboard
  // copy and stays tight; `agentHint` (~1024) carries the Anthropic-rubric
  // "when to use / scenario / caveats" guidance and is surfaced ONLY into the
  // served tool description the model reads (see `tool-description.ts` /
  // `composeDescription`), never the dashboard human copy. Optional + additive;
  // no producer sets it yet (CS-020 / CLI enrichment light it up). Bounded so
  // the `tools/list` token budget is never blown.
  agentHint: z.string().max(1024).optional(),
  // CS-101b — a capability-level worked-example channel (bounded count +
  // per-example size). Pure schema substrate today: folded to `semantics` on
  // upload and read back permissively; the near-term consumers are CS-020's
  // EasyTool contract + CS-117's example-arg generation. See
  // {@link CapabilityExamplesSchema}.
  examples: CapabilityExamplesSchema.optional(),
  sideEffect: SideEffectSchema.optional(),
  risk: RiskSchema.optional(),
  // Permissions
  requiresAuth: z.boolean().default(true),
  requiredScopes: z.array(z.string()).default([]),
  // Catalog-level admin flag (Phase 1 pass 8 .syncanix.yaml override; default false)
  internal: z.boolean().default(false),
  // Step-up auth (Phase 4 pass 5 — iter 170 lays the schema field; the
  // server-side intent issuer + customer-API verifier light up in
  // follow-up iters). When true, the agent loop sets
  // `IntentPayload.requiresStepUp` on the issued intent envelope; the
  // customer's API uses the SDK's step-up verifier to require a fresh
  // MFA / WebAuthn challenge before executing the capability.
  requiresStepUp: z.boolean().default(false),
  // AF-342 — data freshness classification. See DataFreshnessSchema above.
  // Absence = unknown / not yet classified.
  dataFreshness: DataFreshnessSchema.optional(),
  // act-as-user PR-B0 (transport-parity plan §4.1) — WebSocket reply-shape.
  // Set ONLY for `WS_HANDLER` capabilities by the CLI WebSocket extractor's
  // static inference. The descriptor-emit path threads it onto the `ws`
  // operation so the executor knows whether the handler replies via a socket.io
  // ack, a paired response event, or not at all (`none` ⇒ degrade). Absent for
  // every non-WS transport. See {@link WsReplySchema}.
  wsReply: WsReplySchema.optional(),
  // PR-2.9 (R2-MCP1) — the request BODY encoding a REST endpoint expects when it
  // is not JSON, populated by the spec-import (OpenAPI `requestBody.content`) and
  // CLI (`enctype`) extractors. The descriptor-emit path (`pendingWidgetExecution`)
  // threads it onto the `rest` operation so the widget executor form-encodes a
  // `form` body and degrades a `multipart`/`binary` one honestly rather than
  // sending JSON to a form endpoint. Absent = `json` (the common case), and
  // absent for every non-REST transport. Rides `semantics.bodyEncoding` (folded
  // by `prepareUploadCapability`, read back by `marshalRowToCapability`) so it
  // needs no migration, exactly like `wsReply`/`streaming`. See
  // {@link RestBodyEncodingSchema}.
  bodyEncoding: RestBodyEncodingSchema.optional(),
  // CS-101e — Server-Sent-Events streaming descriptor. Set ONLY for `SSE_STREAM`
  // capabilities by the CS-314 SSE extractor's static inference: the stream
  // direction (`server-to-client`) plus the optional per-event `payloadShape`.
  // Absent for every non-SSE transport. Folded into `semantics.streaming` on
  // upload (mirror of the `webhook` / `job` / `examples` fold in
  // `prepareUploadCapability`) and read back permissively by
  // `marshalRowToCapability`. No producer sets it yet. See
  // {@link StreamingDescriptorSchema}.
  streaming: StreamingDescriptorSchema.optional(),
  // CS-101d — locale-keyed semantics: per-locale variants of the agent/human-
  // facing name / title / description (+ per-arg descriptions), `en`-fallback.
  // Rides the open `semantics` jsonb (migration-free + SDL-free), folded into
  // `semantics.localized` on upload (mirror of the `examples` / `streaming` fold
  // in `prepareUploadCapability`) and read back permissively by
  // `marshalRowToCapability`. Pure schema substrate — no producer sets it yet
  // (CS-113 lights it up). See {@link CapabilityLocalizationSchema}.
  localized: CapabilityLocalizationSchema.optional(),
  // CS-116 — Re-Invoke synthetic query expansion: per-locale synthetic user
  // utterances indexed server-side as retrieval keys. Rides the open `semantics`
  // jsonb (migration-free + SDL-free), folded to `semantics.syntheticQueries` on
  // upload + read back permissively. Substrate — the enrichment producer +
  // retrieval indexer are follow-ups. See {@link SyntheticQueriesSchema}.
  syntheticQueries: SyntheticQueriesSchema.optional(),
  // CS-101g — optional operational hints an agent can use to plan a call:
  // `latencyMs` (typical round-trip latency, a positive-integer millisecond
  // value — see {@link LatencyMsSchema}) and `idempotent` (whether repeating the
  // call is side-effect-safe, so the agent can retry a timeout without risking a
  // double-write). Both ride the open `semantics` jsonb (migration-free +
  // SDL-free), folded on upload and read back permissively by
  // `marshalRowToCapability`. Pure schema substrate — no producer sets them yet.
  latencyMs: LatencyMsSchema.optional(),
  idempotent: z.boolean().optional(),
  // CS-013c — dashboard row-ordering priority, authored via the per-capability
  // `priority: high|normal|low` rule in `.syncanix.yaml` (threaded onto the
  // capability at `init` by `applyPriority`, previously dropped on the floor).
  // A governance concern, NOT agent-facing: it rides the open `semantics` jsonb
  // (migration-free + SDL-free), folded into `semantics.priority` on upload and
  // read back to the DASHBOARD verbatim through the `Capability.semantics` JSON
  // scalar — it is NOT surfaced into the served tool description. See
  // {@link CapabilityPrioritySchema}.
  priority: CapabilityPrioritySchema.optional(),
  // Handler text — capped at 2 KB; sent to LLM in Phase 1 pass 7 under ZDR
  // contract. Secret-redactor must have run before this string is populated.
  handlerText: z.string().max(2048),
  // AF-37 (iter 414) — webhook annotation. Populated by the CLI's
  // `detectWebhook(...)` helper during the framework-extractor pass.
  // Absence = endpoint is NOT a webhook receiver; presence = inbound
  // webhook with provider + confidence. The chat orchestrator + MCP
  // server read this to decide whether to expose the endpoint as an
  // LLM-callable tool (webhooks are external triggers, not tools the
  // agent can usefully invoke).
  webhook: WebhookAnnotationSchema.optional(),
  // AF-35 (iter 419) — background-job annotation. Populated by the
  // CLI's `detectBackgroundJob(...)` helper during the framework-
  // extractor pass. Absence = endpoint does NOT enqueue async work;
  // presence = endpoint triggers a job via the named provider
  // (Bull / Inngest / Temporal). The dashboard surfaces this as
  // "triggers async work" labelling; the chat orchestrator treats
  // these endpoints as "fire-and-forget" tools (the agent can call
  // them, but the response is the enqueue confirmation, not the
  // job's eventual outcome).
  job: JobAnnotationSchema.optional(),
  // AF-36 — auth-flow annotation. Populated by the CLI's
  // `detectAuthFlow(...)` helper during the framework-extractor
  // pass. Absence = no auth signal detected (framework-level
  // `requiresAuth` default still applies); presence = strategy
  // (jwt / session / api-key / oauth / public / unknown) + optional
  // provider tag (auth0 / clerk / cognito / workos / supertokens).
  // The dashboard surfaces per-endpoint credential requirements;
  // the chat orchestrator + MCP server use this to decide how to
  // propagate (or omit) the user's credentials when invoking the
  // endpoint on the agent's behalf.
  auth: AuthFlowAnnotationSchema.optional(),
  // Pagination annotation. Populated by the CLI's `detectPagination(...)`
  // helper (query-param names + handler-body signals). Absence = not a
  // paginated collection endpoint (or undetectable); presence = style
  // (cursor / offset / page / link-header) + driving param. The agent reads
  // it to fetch the NEXT page instead of assuming it saw the whole set.
  pagination: PaginationAnnotationSchema.optional(),
  // Rate-limit annotation. Populated by the CLI's `detectRateLimit(...)`
  // helper (limiter decorators / middleware / rate-limit headers). Absence =
  // no rate-limit signal detected; presence = how it was detected. The agent
  // reads it to pace calls rather than hammering the endpoint into a 429.
  rateLimit: RateLimitAnnotationSchema.optional()
});
var MAX_HANDLER_TEXT_BYTES = 2048;
function collectGraphqlArgTypes(capability) {
  const argTypes = {};
  const args = [
    ...capability.pathParams,
    ...capability.queryParams,
    ...capability.body?.shape ?? []
  ];
  for (const arg of args) {
    if (arg.sdlType !== void 0 && arg.sdlType !== "") {
      argTypes[arg.name] = arg.sdlType;
    }
  }
  return Object.keys(argTypes).length > 0 ? argTypes : void 0;
}
function collectGraphqlInputObject(capability) {
  return capability.body?.graphqlInputObject;
}

export { ArgSchema, ArgTypeSchema, AuthFlowAnnotationSchema, AuthProviderSchema, AuthStrategySchema, CAPABILITY_ID_PATTERN, CAPABILITY_PROVENANCES, CapabilityExampleSchema, CapabilityExamplesSchema, CapabilityLocalizationSchema, CapabilityPrioritySchema, CapabilityProvenanceSchema, CapabilitySchema, CapabilitySourceSchema, FRONTEND_DERIVED_FRAMEWORKS, FrameworkSchema, GraphQLOpSchema, GraphqlInputObjectSchema, GrpcOpSchema, HttpMethodSchema, JobAnnotationSchema, JobProviderSchema, LOCALIZED_ARG_DESCRIPTION_MAX, LOCALIZED_DESCRIPTION_MAX, LOCALIZED_TITLE_MAX, LatencyMsSchema, LocalizedTextSchema, MAX_CAPABILITY_EXAMPLES, MAX_HANDLER_TEXT_BYTES, MAX_SYNTHETIC_QUERIES, MethodSchema, PaginationAnnotationSchema, PaginationStyleSchema, RateLimitAnnotationSchema, RateLimitSourceSchema, RestBodyEncodingSchema, RiskSchema, SchemaSourceSchema, ShapeSchema, SideEffectSchema, SseOpSchema, StreamingDescriptorSchema, SyntheticQueriesSchema, TrpcOpSchema, WebhookAnnotationSchema, WebhookConfidenceSchema, WebhookKindSchema, WebhookProviderSchema, WsOpSchema, WsReplySchema, collectGraphqlArgTypes, collectGraphqlInputObject, isFrontendDerivedFramework, isPublicCapability };
//# sourceMappingURL=chunk-ZY5LZSWZ.js.map
//# sourceMappingURL=chunk-ZY5LZSWZ.js.map